import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';

const FORM_URL = 'https://readdy.ai/api/form/da1gcgomlpfrg29fphm0';

const principles = [
  { icon: 'ri-filter-3-line', title: '分类清晰', desc: '按题材、类型系统整理国漫与日漫，让找漫画像逛超市一样简单。' },
  { icon: 'ri-checkbox-circle-line', title: '内容可靠', desc: '持续跟进热门连载与经典作品，收录信息力求准确、及时。' },
  { icon: 'ri-refresh-line', title: '更新及时', desc: '每日追踪最新章节进度，追更作品第一时间呈现最新动态。' },
  { icon: 'ri-hand-heart-line', title: '尊重版权', desc: '本站仅提供分类索引与内容介绍，尊重并引导支持原创作者。' },
];

const reviews = [
  { name: '爱吃瓜的栗子', text: '分类特别清楚，想找玄幻或者武侠直接点进去就行，不用到处搜。' },
  { name: '夜航星', text: '国漫和日漫分开逛太方便了，追了好几年的连载进度一眼就能看到。' },
  { name: '三川', text: '编辑推荐和人气榜单挺靠谱的，按评分筛基本没踩过坑。' },
  { name: '小满', text: '界面看着舒服，找漫画效率高，新作上架信息更新得也快。' },
];

export default function About() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== 'website_alt' && value !== '') payload.append(key, String(value));
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(msg ? `提交未成功：${msg}` : '提交失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后再试');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://oiutc07.cn/' },
                  { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://oiutc07.cn/about' },
                ],
              },
              {
                '@type': 'AboutPage',
                name: '关于虫虫漫画',
                url: 'https://oiutc07.cn/about',
                description: '虫虫漫画是专注国漫与日漫分类整理的漫画索引网站，致力于让找漫画变得更简单。',
              },
            ],
          }),
        }}
      />
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 md:px-6 py-8">
        <nav className="text-sm text-foreground-500" aria-label="面包屑">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-foreground-700" aria-current="page">关于我们</li>
          </ol>
        </nav>

        <section className="mt-6 overflow-hidden rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 text-background-50">
          <div className="px-6 py-12 md:px-10 md:py-14">
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm">
              <i className="ri-bug-2-line"></i>虫虫漫画
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl">把找漫画这件小事，做到极致</h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-background-50/90">
              虫虫漫画网是一个专注国漫与日漫分类整理的漫画索引网站。我们按题材、类型系统梳理热门连载与经典作品，让每一位读者都能快速找到想看的漫画。
            </p>
          </div>
        </section>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl md:text-2xl text-foreground-950">
              <i className="ri-compass-3-line text-primary-500"></i>网站定位
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们想做漫画阅读的「分类导航」。当你想看玄幻、武侠、恋爱或是治愈系作品时，不用再一个个平台去翻，打开虫虫漫画，按分类就能逛完整个题材图谱。
            </p>
            <h3 className="mt-5 font-medium text-foreground-900">发展理念</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              从「分类准确」到「信息及时」，再到「阅读友好」，我们一步一个脚印地打磨内容结构。未来会持续扩充题材细分、优化榜单维度，让找漫画这件事真正又快又省心。
            </p>
          </section>

          <section className="rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl md:text-2xl text-foreground-950">
              <i className="ri-quill-pen-line text-accent-500"></i>内容原则
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {principles.map((p) => (
                <div key={p.title} className="rounded-xl bg-background-50 p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={p.icon}></i>
                  </span>
                  <h4 className="mt-3 font-medium text-foreground-900">{p.title}</h4>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{p.desc}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <section className="mt-10">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
            用户<span className="text-primary-500">口碑</span>
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {reviews.map((r) => (
              <figure key={r.name} className="rounded-2xl border border-background-200 bg-background-100 p-5">
                <div className="flex items-center gap-1 text-accent-500" aria-label="5星好评">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-600">“{r.text}”</blockquote>
                <figcaption className="mt-4 text-xs text-foreground-500">— {r.name}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
              联系<span className="text-primary-500">我们</span>
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              有想看的作品、更好的分类建议，或遇到任何问题，都欢迎通过邮箱与我们联系。你的每一次反馈，都在帮我们把虫虫漫画做得更好。
            </p>
            <div className="mt-5 flex items-center gap-3 rounded-xl bg-background-50 p-4 text-sm">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-mail-line"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">建议 / 反馈邮箱</p>
                <a href="mailto:support@chongchong-manga.com" className="font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                  support@chongchong-manga.com
                </a>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
              给我们<span className="text-accent-500">留言</span>
            </h2>
            <form data-readdy-form="虫虫漫画联系我们表单" onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input type="text" name="name" placeholder="你的称呼" maxLength={50} className="h-11 rounded-lg border border-background-200 bg-background-50 px-4 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200" aria-label="称呼" />
                <input type="email" name="email" placeholder="你的邮箱" maxLength={100} className="h-11 rounded-lg border border-background-200 bg-background-50 px-4 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200" aria-label="邮箱" />
              </div>
              <textarea name="message" rows={4} maxLength={500} placeholder="想说的话（最多 500 字）" className="w-full rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200" aria-label="留言内容"></textarea>
              <div className="field-honeypot" aria-hidden="true">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
              </div>
              <button type="submit" className="inline-flex h-11 items-center gap-2 rounded-full bg-primary-500 px-7 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors">
                发送留言 <i className="ri-send-plane-line"></i>
              </button>
              {status === 'success' && (
                <p className="flex items-center gap-2 rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line"></i>感谢你的反馈，我们会尽快查看！
                </p>
              )}
              {status === 'error' && (
                <p className="flex items-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line"></i>{formError}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}